/**
 * DataGrid — the editable grid behind Drivers, Vehicles and Vehicle Inventory.
 *
 * Three things shaped every decision in here.
 *
 * 1. THERE IS NO BULK ENDPOINT. Saving forty edited rows is forty separate
 *    requests. So nothing is written as you type: edits collect in a dirty
 *    buffer, you press Save, and a concurrency-limited queue works through
 *    them carrying per-row status. When row seven of forty fails, rows one to
 *    six and eight to forty stay saved and row seven stays dirty, red and
 *    retryable. A single "Saved" toast over a partial failure would be a lie.
 *
 * 2. IT BEHAVES LIKE A SPREADSHEET, because that is both what people expect
 *    and what the ARIA grid pattern prescribes: one tab stop for the whole
 *    grid (roving tabindex), arrows to move, Enter/F2/typing to edit, Escape
 *    to revert the cell, Tab to commit and step on.
 *
 * 3. AT 15-50 ROWS, DO NOT VIRTUALISE. Virtualisation below a hundred rows is
 *    net overhead and it forces aria-rowindex bookkeeping. What actually costs
 *    frames at this size is re-render breadth, so the in-progress editor keeps
 *    its own value and rows are memoised.
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, ChevronsUpDown, Columns, LayoutGrid, Table2, X } from 'lucide-react';
import { successToast, errorToast } from '../utils/validate';

/* ── The lens switch ─────────────────────────────────────────────────────────
   One page, two ways of looking at it. The route does not change, the filters
   do not reset, and which lens you last used is remembered — so a dispatcher
   who lives in the grid and an owner who prefers the board each get their own
   page back without ever configuring anything. */

/* On a phone every page opens in Figure view (Round 4, Oct 2026): a wide
   table is the one thing a 375px screen cannot show well. The phone's own
   choice is not written back, so a desktop in the same browser profile keeps
   the lens it last used; switching to Table view on the phone still works. */
const isPhoneWidth = () => {
  try { return window.matchMedia('(max-width: 720px)').matches; } catch (_) { return false; }
};

export function useLens(storageKey, initial = 'grid') {
  const phone = useRef(isPhoneWidth());
  const [lens, setLens] = useState(() => {
    if (phone.current) return 'visual';
    try { return window.localStorage.getItem(storageKey) || initial; }
    catch (_) { return initial; }
  });
  useEffect(() => {
    if (phone.current) return;
    try { window.localStorage.setItem(storageKey, lens); } catch (_) { /* fine */ }
  }, [storageKey, lens]);
  return [lens, setLens];
}

/* THE SAME TWO WORDS ON EVERY PAGE. Each page used to name its second lens
   after its own figure (Runway, Ladder, Timeline, Tree, Cards, Vans, ...), so
   the one control that works identically everywhere had thirteen labels to
   learn. The figure's own name lives in its title; the switch only says which
   way of looking you are in. Pages may still pass `visualLabel`; it is no
   longer shown. */
/* ── UNSAVED TABLE EDITS (Round 4, Oct 2026) ─────────────────────────────
   Every grid on screen reports how many edits it is holding. Leaving the
   table (the Figure view switch, a link to another page, closing the tab)
   asks first when any are waiting for Save, instead of dropping them. */
const pendingGrids = new Map();
let gridSeq = 0;
export function unsavedGridChanges() {
  let n = 0;
  pendingGrids.forEach((v) => { n += v; });
  return n;
}
export function confirmLeaveGrid() {
  const n = unsavedGridChanges();
  return n === 0 || window.confirm(
    `You have ${n} change${n === 1 ? '' : 's'} in the table that ${n === 1 ? 'is' : 'are'} not saved. Leave without saving?`,
  );
}

export function LensSwitch({ value, onChange }) {
  return (
    <div className="lens" role="group" aria-label="How to view this page">
      <button type="button" aria-pressed={value === 'grid'} onClick={() => onChange('grid')}>
        <Table2 size={13} /> Table view
      </button>
      <button type="button" aria-pressed={value === 'visual'}
              onClick={() => { if (value === 'grid' && !confirmLeaveGrid()) return; onChange('visual'); }}>
        <LayoutGrid size={13} /> Figure view
      </button>
    </div>
  );
}

/* ── Column model ───────────────────────────────────────────────────────────
   type      text | number | select | date | readonly
   options   for select: [{ value, label }]
   bulk      appears in the "set for all selected" bar
   validate  (value, row) => message | null   hard: red, and blocks Save
   warn      (value, row) => message | null   soft: amber, saves anyway
   parse     (rawString, row) => value          used by typing and by paste
   render    (row) => node                      display only                */

/* `locked` is set by the grid itself, never by a page: with no `onCommit`
   there is nowhere to save to, so every cell is read-only whatever its type.
   That is how a page with no edit permission switches inline editing, paste
   and the bulk bar off — it just doesn't pass `onCommit`. */
const isEditable = (col) => col && !col.locked && col.type && col.type !== 'readonly';

/** Cells hold values; the display may be a label. This is what paste and
 *  typing convert into. */
function parseValue(col, raw, row) {
  if (col.parse) return col.parse(raw, row);
  const s = String(raw ?? '').trim();
  if (col.type === 'number') {
    if (s === '') return null;
    const n = Number(s.replace(/[, ]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  if (col.type === 'select') {
    const hit = (col.options || []).find(o =>
      String(o.value) === s || String(o.label).toLowerCase() === s.toLowerCase());
    // Real data outlives an options list. Vehicles typed "Truck" before the
    // six canonical types existed, drivers carry statuses this page never
    // offered. Returning null for those would mean that merely clicking a
    // dropdown and clicking away silently wipes the field — so an
    // unrecognised value is kept exactly as it is, and only an empty string
    // clears the cell.
    return hit ? hit.value : (s === '' ? null : s);
  }
  return s;
}

/* "Is this the same value?" is not string equality against the raw record.
   A date arrives from the server as 2026-09-06T00:00:00 and a date editor
   produces 2026-09-06; comparing those raw made every date cell dirty the
   moment it was opened, so pressing Enter on an unchanged date queued a
   full-record write. Both sides go through the column's own parse first. */
function sameValue(col, row, a, b) {
  const norm = (v) => {
    if (v == null || v === '') return '';
    return String(col.parse ? col.parse(v, row) : v);
  };
  return norm(a) === norm(b);
}

function displayValue(col, row, value) {
  const v = value !== undefined ? value : row[col.key];
  if (col.type === 'select') {
    const hit = (col.options || []).find(o => String(o.value) === String(v));
    return hit ? hit.label : (v ?? '');
  }
  if (col.type === 'number') return v == null || v === '' ? '' : Number(v).toLocaleString();
  return v ?? '';
}

/* Module scope, not default-parameter expressions.

   Written inline as `rowId = (r) => r.id`, the default evaluates to a NEW
   function on every render. It sits in the dependency array of `valueOf`,
   `setCell`, `save`, `blocking`, `onKeyDown`, `applyBulk` and both rows
   effects — so none of them were ever stable, and `blocking` re-ran every
   column's `validate` over every dirty cell on every keystroke. None of the
   three pages passes `rowId`, so this was the live path. */
const defaultRowId = (r) => r.id;
const defaultRowLabel = (r) => r.name || `#${defaultRowId(r)}`;

/* ── Undo group ids ──────────────────────────────────────────────────────
   A counter, not `Date.now()`. Two operations of the same kind inside one
   millisecond produced the same id and merged into a single undo step — and
   a paste applies its whole plan in one tick, so this was not hypothetical. */
let groupSeq = 0;
const nextGroup = () => { groupSeq += 1; return groupSeq; };

/* ── Clipboard: a real TSV reader ────────────────────────────────────────
   Excel, Numbers and Sheets all write RFC4180-style quoting into the
   `text/plain` flavour: a cell containing a tab, a newline or a quote arrives
   wrapped in double quotes, with embedded quotes doubled. Splitting naively on
   "\n" then "\t" tore one such cell in half and shifted every row below it up
   by one — so a 40-row paste showed a plausible first eight in the preview and
   wrote the rest into the wrong records. The preview cannot save you from a
   parser that has already misaligned the data.

   Walks the string once. Outside quotes, tab ends a cell and newline ends a
   row; inside quotes, both are literal and "" is an escaped quote. */
function parseClipboardGrid(text) {
  const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = [];
  let cell = '';
  let row = [];
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 1; }
        else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === '') { quoted = true; continue; }
    if (ch === '\t') { row.push(cell); cell = ''; continue; }
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }
  row.push(cell);
  rows.push(row);
  // A trailing newline leaves one empty trailing row; drop it, but never drop
  // a deliberate blank cell in the middle of a block.
  if (rows.length > 1 && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') rows.pop();
  return rows;
}

/* ── A queue that will not open forty sockets at once ────────────────────── */
async function runQueue(items, worker, limit = 5) {
  const results = new Array(items.length);
  let next = 0;
  const lane = async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      try { results[i] = { ok: true, value: await worker(items[i], i) }; }
      catch (err) { results[i] = { ok: false, error: err }; }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
  return results;
}

/* ── Which columns, in what order, remembered per user per page ──────────
   A stored order can go stale — a release adds a column, renames one, drops
   one — so it is treated as a preference laid over the current definition,
   never as the definition itself. Keys that no longer exist are dropped;
   keys the store has never seen keep the position the code gives them. */
const defaultState = (columns) => ({
  hidden: columns.filter(c => c.hidden).map(c => c.key),
  order: columns.map(c => c.key),
  widths: {},
  known: columns.map(c => c.key),
});

function useColumnState(storageKey, columns) {
  const [state, setState] = useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) return { ...defaultState(columns), ...JSON.parse(raw) };
    } catch (_) { /* private mode, quota, whatever — defaults are fine */ }
    return defaultState(columns);
  });

  /* A column the stored preference has never seen is a column the user has
     never had an opinion about, so it takes the default the code gives it.
     Without this, adding a hidden-by-default column ships it visible to
     everyone who has ever opened the page — their saved `hidden` list simply
     doesn't mention it, and "not mentioned" would read as "show it". */
  useEffect(() => {
    setState(s => {
      const live = new Set(columns.map(c => c.key));
      const known = new Set(s.known || []);
      const fresh = columns.filter(c => !known.has(c.key));
      // A key for a column that no longer exists makes the "N hidden" badge
      // and the "N of M shown" line count things that are not there.
      const stale = s.hidden.filter(k => !live.has(k));
      if (!fresh.length && !stale.length) return s;
      return {
        ...s,
        hidden: [
          ...s.hidden.filter(k => live.has(k)),
          ...fresh.filter(c => c.hidden).map(c => c.key),
        ],
        known: columns.map(c => c.key),
      };
    });
  }, [columns]);

  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify(state)); }
    catch (_) { /* not worth failing over */ }
  }, [storageKey, state]);
  return [state, setState];
}

export default function DataGrid({
  rows,
  columns: declaredColumns,
  rowId = defaultRowId,
  rowLabel = defaultRowLabel,
  onCommit,                 // async (row, changes) => void   one row
  onSaved,                  // () => void   once, after the whole queue
  storageKey = 'grid',
  defaultSort = null,       // { key, dir } — what the page opens on
  empty = null,
  emptyFiltered = 'No rows match the current filters.',
  note = null,              // accepted but unused — see the Columns block below
  hint = null,              // accepted but unused — see the Columns block below
  toolsSlot = null,         // ref to a span in the page header for the Columns button
  onToast,
}) {
  const rootRef = useRef(null);
  const gearRef = useRef(null);
  const allColumns = useMemo(
    () => (onCommit ? declaredColumns : declaredColumns.map(c => ({ ...c, locked: true }))),
    [declaredColumns, onCommit],
  );
  const [colState, setColState] = useColumnState(storageKey, allColumns);
  const [showCols, setShowCols] = useState(false);

  /* A column's BASE width: what the page declared, or what the user dragged it
     to. Declared up here because the resize handler (far above the layout
     code) and the width calculation (far below it) both need it, and a `const`
     between them would put one of the two in the temporal dead zone. */
  const baseWidth = useCallback(
    (col) => (colState.widths[col.key] || col.width || 120),
    [colState.widths],
  );

  /* ── Depth on the pinned edges ────────────────────────────────────────────
     The frozen first and last columns are opaque and sit on top of the rest
     of the table, so at any scroll position other than the ends they are
     quietly covering data — a half-hidden quantity looks like a rendering
     fault, not like a column continuing underneath.

     A 1px border cannot say "there is more under here"; a shadow can. It has
     to be conditional, though: a shadow on the left edge while the table is
     scrolled fully left is drawing a depth that is not there. So the two
     edges light up independently, each when there is actually something
     hidden on its side. */
  const scrollRef = useRef(null);
  const [xEdges, setXEdges] = useState({ start: false, end: false });
  /* How wide the scroller actually is. Needed because `table.dg` is
     `width: 100%` with `table-layout: fixed`, and that combination treats the
     declared column widths as PROPORTIONS whenever their total is narrower
     than the container: the browser hands the surplus to every column at once.
     On a table with a dozen columns the total overflows and nothing stretches,
     which is why Drivers always looked right — but a sparse table like Items
     or Zones inflated every column, so "Clear" sat alone in a 300px cell and
     the money drifted half a screen from its own heading. */
  const [scrollW, setScrollW] = useState(0);

  const syncEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Round: sub-pixel layout leaves ~0.5px of "scroll" at both extremes,
    // which would light both shadows on a table that does not scroll at all.
    const max = Math.round(el.scrollWidth - el.clientWidth);
    const at = Math.round(el.scrollLeft);
    setXEdges(prev => {
      const next = { start: at > 1, end: max > 1 && at < max - 1 };
      return prev.start === next.start && prev.end === next.end ? prev : next;
    });
    setScrollW(prev => {
      const w = Math.round(el.clientWidth);
      return prev === w ? prev : w;
    });
  }, []);

  const onScrollX = useCallback(() => syncEdges(), [syncEdges]);

  /* Resizing, hiding and reordering a column all change whether the table
     overflows, and none of them fire a scroll event. ResizeObserver catches
     the lot, plus the window getting narrower. */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    syncEdges();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(syncEdges);
    ro.observe(el);
    const table = el.firstElementChild;
    if (table) ro.observe(table);
    return () => ro.disconnect();
  }, [syncEdges]);

  /* `markLearned` is a no-op now, and deliberately kept as one.

     It existed to retire the "click a cell and type" hint after a user's first
     edit, writing a flag to localStorage. The hint itself is gone — a
     spreadsheet grid teaches that in one click — so there is nothing left to
     retire. `setCell` still calls this on every write, so the function stays
     rather than threading a removal through the whole edit path; it now costs
     nothing instead of a synchronous disk write per cell. */
  const markLearned = useCallback(() => {}, []);

  /* The user's order, reconciled against the columns that actually exist. */
  const ordered = useMemo(() => {
    const byKey = new Map(allColumns.map(c => [c.key, c]));
    const seen = new Set();
    const out = [];
    (colState.order || []).forEach(k => {
      const c = byKey.get(k);
      if (c && !seen.has(k)) { out.push(c); seen.add(k); }
    });
    allColumns.forEach(c => { if (!seen.has(c.key)) out.push(c); });
    /* Two anchors. The identity column holds the left edge — drag it into
       the middle and a scrolled row has nothing to be read by. The actions
       column holds the right, because it is the way out of the table to the
       record, and a way out you have to scroll sideways to find is not much
       of a way out. Everything between them is the user's to arrange. */
    const start = out.filter(c => c.frozen);
    const end = out.filter(c => c.frozenEnd);
    const middle = out.filter(c => !c.frozen && !c.frozenEnd);
    return [...start, ...middle, ...end];
  }, [allColumns, colState.order]);

  /* A pinned column is never hidden, whatever the stored preference says. The
     Columns panel disables the checkbox for pinned columns, so a key that ends
     up in `hidden` AND pinned — because a release added `frozen: true` to a
     column some users had already hidden — was invisible with no control that
     could bring it back, short of Reset to default. And the table lost its
     sticky edge while the scroll shadows kept implying one. */
  const columns = useMemo(
    () => ordered.filter(c => c.frozen || c.frozenEnd || !colState.hidden.includes(c.key)),
    [ordered, colState.hidden],
  );

  /* Row ticks exist to aim a bulk action. With nothing editable and no bulk
     column there is nothing to aim, so the checkbox column would be a control
     that does nothing — worse than absent. Vehicle Inventory is read-only
     until the Set-quantity endpoint lands, and this is what makes it read
     honestly as a ledger rather than as a grid with its edits broken.
     Derived from the VISIBLE columns, not from `allColumns`: the bulk bar
     only offers columns that are on screen, so hiding every bulk column left
     the ticks, the "N selected" bar and Clear selection in place with zero
     actions behind them — the exact control-that-does-nothing this is here to
     prevent. */
  /* ── Column resize ────────────────────────────────────────────────────
     Drag the divider on a header's trailing edge. `colState.widths` has been
     persisted since the beginning and nothing ever wrote to it; this is what
     writes to it.

     The drag runs on window listeners rather than on the handle, because the
     pointer leaves a 6px target almost immediately and a handler bound to
     the element stops receiving moves the moment it does. */
  const resizeRef = useRef(null);
  const [resizing, setResizing] = useState(null);   // column key, for the cursor

  /* startW is the column's BASE width, not its rendered width.

     A column renders at `base + slack`. The drag writes `startW + delta` back
     into `colState.widths`, which is the base — so seeding startW from
     getBoundingClientRect(), which includes the slack, fed the slack back in
     as base and then had it added a second time on render. Asking for +10px
     grew the column by 17, then by 14 again on the next drag, and the
     inflation persisted to localStorage. Measured, not theorised. */
  const startResize = useCallback((e, col) => {
    e.preventDefault();
    e.stopPropagation();          // must not reach the header's sort button
    resizeRef.current = {
      key: col.key,
      startX: e.clientX,
      startW: baseWidth(col),
    };
    setResizing(col.key);
  }, [baseWidth]);

  /* The live width during a drag, kept OUT of colState.

     Writing colState on every mousemove re-rendered every row and every cell,
     JSON.stringify'd the whole column state and hit localStorage
     synchronously — at 60–120 events a second. The drag now moves a single
     number; colState is written once, on release. */
  const [dragW, setDragW] = useState(null);   // { key, w } | null

  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const r = resizeRef.current;
      if (!r) return;
      // 64px floor: narrower than that and the header label and the sort
      // chevron collide, which looks broken rather than compact. 720 ceiling
      // so one column cannot be dragged out past the whole viewport.
      const next = Math.min(720, Math.max(64, Math.round(r.startW + (e.clientX - r.startX))));
      setDragW({ key: r.key, w: next });
    };
    const onUp = () => {
      const r = resizeRef.current;
      setDragW(live => {
        if (r && live && live.key === r.key) {
          // Committed outside the updater — this one is not pure otherwise.
          queueMicrotask(() => setColState(s => ({ ...s, widths: { ...s.widths, [r.key]: live.w } })));
        }
        return null;
      });
      resizeRef.current = null;
      setResizing(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    /* Release the button outside the window and no mouseup is delivered —
       the drag stayed armed, and moving back over the page resumed resizing
       with nothing held down. */
    window.addEventListener('blur', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
    };
  }, [resizing, setColState]);

  /* ── Column reorder, by dragging the header itself ─────────────────────
     The columns panel could already reorder, but only once you had found the
     panel. Dragging the thing you want to move is the gesture people try
     first.

     Frozen and end-pinned columns are excluded: they are pinned to an edge,
     so "move me to position 4" has no meaning for them, and letting one be
     dropped into the middle would leave a sticky column floating over the
     scroll. */
  const [dragCol, setDragCol] = useState(null);
  const [dropCol, setDropCol] = useState(null);
  const canReorder = (col) => !col.frozen && !col.frozenEnd && Boolean(col.label);

  const onHeaderDrop = useCallback((target) => {
    setDropCol(null);
    const from = dragCol;
    setDragCol(null);
    if (!from || from === target) return;
    setColState(s => {
      /* Reordering acts on the FULL order, not just the visible columns, so a
         hidden column keeps its place relative to its neighbours and does not
         jump to the end the next time it is shown.

         Spliced against `ordered` — the RECONCILED list — rather than the
         stored `s.order`. A stored order predates any column added since it
         was written, so that column was missing from `s.order`, `indexOf`
         returned -1, and the drag was a silent no-op: the header lifted, the
         drop line appeared, and nothing moved. Starting from the reconciled
         list also repairs the stored order as a side effect, which is what the
         Columns panel's reorder already did. */
      const order = ordered.map(c => c.key);
      const fi = order.indexOf(from);
      const ti = order.indexOf(target);
      if (fi === -1 || ti === -1) return s;
      order.splice(fi, 1);
      order.splice(order.indexOf(target) + (ti > fi ? 1 : 0), 0, from);
      return { ...s, order };
    });
  }, [dragCol, setColState, ordered]);

  /* ── Sorting ─────────────────────────────────────────────────────────── */
  // A table that opens in the order the server happened to return is a table
  // you have to work before it tells you anything. Pages set what matters.
  // Held in a ref because the pages pass a fresh object literal every render;
  // reading it from an effect's dep array would re-run the effect forever.
  const defaultSortRef = useRef(defaultSort);
  const [sort, setSort] = useState(defaultSort || { key: null, dir: 'asc' });
  /* Hide the column you are sorted by and the sort survives, but every header
     then reports aria-sort="none" and no chevron shows — the rows sit in an
     order with nothing on screen accounting for it. `_attention` is the
     default sort on all three pages and is hideable, so this was one click
     away. Falling back to the page's own default keeps a stated order.

     The fallback has to check that the default is still VISIBLE. Hiding
     `_attention` — which is itself the default on Drivers, Vehicles and
     Inventory — fell back to `_attention`, i.e. straight back to the hidden
     column, and the exact case this effect exists for was the one it could not
     fix. Worse, it dead-ended silently: with the sort untouched since mount,
     `sort` and `defaultSortRef.current` are the same object, so setSort hit
     React's bailout and did not even re-render; and had it re-rendered,
     `sort.key` was unchanged so the effect would not run again.

     So: use the default only if its column is on screen, otherwise drop to no
     sort at all, which at least matches what the headers are reporting. */
  useEffect(() => {
    if (!sort.key) return;
    if (columns.some(c => c.key === sort.key)) return;
    const fallback = defaultSortRef.current;
    const usable = fallback && columns.some(c => c.key === fallback.key);
    setSort(usable ? { ...fallback } : { key: null, dir: 'asc' });
  }, [columns, sort.key]);
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = allColumns.find(c => c.key === sort.key);
    if (!col) return rows;
    const val = (r) => (col.sortValue ? col.sortValue(r) : r[col.key]);
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === 'number' && typeof y === 'number'
        ? x - y
        : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? c : -c;
    });
  }, [rows, sort, allColumns]);

  /* ── Cell focus, edit, range ─────────────────────────────────────────── */
  const [focus, setFocus] = useState({ r: 0, c: 0 });
  const [editing, setEditing] = useState(null);   // { r, c, seed }
  const [anchor, setAnchor] = useState(null);     // range start

  /* ── The dirty buffer: rowId -> { colKey: { from, to } } ─────────────── */
  const [dirty, setDirty] = useState(() => new Map());
  const [status, setStatus] = useState(() => new Map()); // rowId -> saving|ok|error
  const [errors, setErrors] = useState(() => new Map()); // rowId -> message
  const undoRef = useRef([]);
  const dirtyRef = useRef(dirty);
  useEffect(() => { dirtyRef.current = dirty; }, [dirty]);

  /* Every row this grid has ever shown, by id. Edits outlive the filter that
     was on screen when they were made — tick a driver's phone, then narrow
     to a different status, and the row leaves `rows` while the change stays
     in the buffer. Resolving saves against only the visible rows dropped
     those edits on the floor. */
  const seenRef = useRef(new Map());
  useEffect(() => {
    rows.forEach(r => seenRef.current.set(String(rowId(r)), r));
  }, [rows, rowId]);

  const dirtyCount = useMemo(
    () => [...dirty.values()].reduce((n, m) => n + Object.keys(m).length, 0),
    [dirty],
  );

  /* Report held edits to the page-wide registry (see confirmLeaveGrid), and
     ask the browser to warn on refresh or tab close while any are held. */
  const [gridKey] = useState(() => ++gridSeq);
  useEffect(() => {
    if (dirtyCount > 0) pendingGrids.set(gridKey, dirtyCount);
    else pendingGrids.delete(gridKey);
    if (dirtyCount === 0) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirtyCount, gridKey]);
  useEffect(() => () => { pendingGrids.delete(gridKey); }, [gridKey]);

  const valueOf = useCallback((row, col) => {
    const d = dirty.get(String(rowId(row)));
    if (d && Object.prototype.hasOwnProperty.call(d, col.key)) return d[col.key].to;
    return row[col.key];
  }, [dirty, rowId]);

  const setCell = useCallback((row, col, next, { push = true, group = null } = {}) => {
    const id = String(rowId(row));
    // The undo entry is recorded here, not inside the updater below — a
    // double-invoked updater would push it twice and one Cmd+Z would then
    // only undo half a change. `group` ties the forty cells of a bulk set or
    // a paste together, so undoing one is one keypress, not forty.
    if (push) {
      undoRef.current.push({ id, key: col.key, group, prev: dirtyRef.current.get(id)?.[col.key] ?? null });
    }
    setDirty(prev => {
      const m = new Map(prev);
      const cur = { ...(m.get(id) || {}) };
      const from = Object.prototype.hasOwnProperty.call(cur, col.key)
        ? cur[col.key].from
        : row[col.key];
      // Back to where it started is not a change.
      if (sameValue(col, row, from, next)) delete cur[col.key];
      else cur[col.key] = { from, to: next };
      if (Object.keys(cur).length) m.set(id, cur); else m.delete(id);
      return m;
    });
    // Touching a row clears whatever the last save said about it.
    setStatus(prev => { if (!prev.has(id)) return prev; const m = new Map(prev); m.delete(id); return m; });
    setErrors(prev => { if (!prev.has(id)) return prev; const m = new Map(prev); m.delete(id); return m; });
    markLearned();
  }, [rowId, markLearned]);

  const undo = useCallback(() => {
    const top = undoRef.current.pop();
    if (!top) return;
    const batch = [top];
    // Everything set in the same stroke comes back in the same stroke.
    if (top.group != null) {
      while (undoRef.current.length && undoRef.current[undoRef.current.length - 1].group === top.group) {
        batch.push(undoRef.current.pop());
      }
    }
    setDirty(prev => {
      const m = new Map(prev);
      batch.forEach(step => {
        const cur = { ...(m.get(step.id) || {}) };
        if (step.prev == null) delete cur[step.key]; else cur[step.key] = step.prev;
        if (Object.keys(cur).length) m.set(step.id, cur); else m.delete(step.id);
      });
      return m;
    });
    /* Clear the last save's verdict on every row this undo touched — the same
       thing `setCell` does, and for the same reason. Without it, undoing a
       failed row's edits until its buffer entry was gone left the row red,
       with its reason line and a Retry button that could only answer "those
       rows are no longer loaded. Reload the page" — about a row sitting right
       there on screen. The save bar stayed up too, because it counts failures. */
    const ids = new Set(batch.map(step => step.id));
    setStatus(prev => {
      if (![...ids].some(id => prev.has(id))) return prev;
      const m = new Map(prev); ids.forEach(id => m.delete(id)); return m;
    });
    setErrors(prev => {
      if (![...ids].some(id => prev.has(id))) return prev;
      const m = new Map(prev); ids.forEach(id => m.delete(id)); return m;
    });
  }, []);

  const discard = useCallback(() => {
    setDirty(new Map());
    setErrors(new Map());
    setStatus(new Map());
    undoRef.current = [];
  }, []);

  /* ── Saving: N requests, per-row status, honest partial failure ──────── */
  const [saving, setSaving] = useState(false);
  const save = useCallback(async (only = null) => {
    if (saving || !onCommit) return;
    const ids = only || [...dirty.keys()];
    if (!ids.length) return;
    const jobs = ids.map(id => ({ id, row: seenRef.current.get(id), changes: dirty.get(id) }))
      .filter(j => j.row && j.changes);

    /* An edited row that has since been filtered out of view is still an
       edit the user made. Resolving against every row ever seen — rather
       than only the rows currently on screen — is what stops it being
       dropped in silence. If one still cannot be resolved, say so; the old
       code counted it as nothing and then reported "0 rows saved" as a
       success. */
    const lost = ids.length - jobs.length;
    if (!jobs.length) {
      onToast?.(errorToast(
        'Nothing could be saved',
        'Those rows are no longer loaded. Reload the page and try again.',
      ));
      return;
    }

    setSaving(true);
    setStatus(prev => { const m = new Map(prev); jobs.forEach(j => m.set(j.id, 'saving')); return m; });

    const results = await runQueue(jobs, async (job) => {
      const patch = {};
      Object.entries(job.changes).forEach(([k, v]) => { patch[k] = v.to; });
      await onCommit(job.row, patch);
      return job.id;
    }, 5);

    /* Counted from `results`, not from inside the setDirty updater. A state
       updater must be pure: React runs it later, and runs it twice in
       development, so counting in there gave a toast that reported zero
       saved rows in production and double in dev. */
    const ok = results.filter(r => r.ok).length;
    const failed = results.length - ok;

    /* Clear only the cells that were actually sent, and only if they still
       hold the value that was sent.

       Nothing stops you typing while a save is in flight, and `prev` here is
       the LATEST buffer, not the one that was snapshotted into `jobs`. The
       old `m.delete(id)` removed the whole row — so a cell edited after its
       request went out was silently dropped: never sent, gone from the
       buffer, gone from the undo stack, and the row painted green as if it
       had been saved. */
    setDirty(prev => {
      const m = new Map(prev);
      results.forEach((res, i) => {
        if (!res.ok) return;
        const { id, changes } = jobs[i];
        const cur = m.get(id);
        if (!cur) return;
        const next = { ...cur };
        Object.entries(changes).forEach(([k, sent]) => {
          if (next[k] && String(next[k].to ?? '') === String(sent.to ?? '')) delete next[k];
        });
        if (Object.keys(next).length) m.set(id, next); else m.delete(id);
      });
      return m;
    });
    // A row that still holds unsent work is not "saved" — it keeps no status,
    // so it does not go green while a pending edit sits in it.
    const stillDirty = new Set(
      results.map((res, i) => (res.ok ? jobs[i] : null))
        .filter(Boolean)
        .filter(job => {
          // dirtyRef, not the `dirty` closure: `save` closed over the buffer as
          // it was when the button was pressed, which by definition cannot
          // contain anything typed since. The ref is what the user has now.
          const cur = dirtyRef.current.get(job.id);
          if (!cur) return false;
          /* Every key the buffer holds, not only the ones that went. A cell
             edited for the FIRST time while the request was in flight is
             absent from `job.changes`, so checking only the sent keys said
             "clean" — the row went green over a live edit, and the undo prune
             below then discarded that edit's history along with the row's.
             The value itself survived in the buffer; Ctrl+Z could no longer
             reach it. */
          return Object.keys(cur).some(k => !job.changes[k]
            || String(cur[k].to ?? '') !== String(job.changes[k].to ?? ''));
        })
        .map(job => job.id),
    );
    setStatus(prev => {
      const m = new Map(prev);
      results.forEach((res, i) => {
        const id = jobs[i].id;
        if (res.ok && stillDirty.has(id)) { m.delete(id); return; }
        m.set(id, res.ok ? 'ok' : 'error');
      });
      return m;
    });
    setErrors(prev => {
      const m = new Map(prev);
      results.forEach((res, i) => {
        if (res.ok) m.delete(jobs[i].id);
        else m.set(jobs[i].id, res.error?.message || 'Could not save');
      });
      return m;
    });
    setSaving(false);
    // Only the history for rows that actually went is spent. After a partial
    // failure the rows still holding changes must still be undoable — the
    // alternative is Discard, which throws away the successful work too.
    undoRef.current = undoRef.current.filter(
      step => !results.some((res, i) => res.ok && jobs[i].id === step.id && !stillDirty.has(step.id)),
    );

    // Everything the list needs re-read, re-read once, after the last row —
    // not once per row from inside each mutation, which fired N refetches
    // and let a later row overwrite what an earlier refetch had brought in.
    if (ok > 0) onSaved?.();

    /* The report says what happened, not what we hoped happened. */
    if (failed === 0 && lost === 0) {
      onToast?.(successToast(
        `${ok} row${ok === 1 ? '' : 's'} saved`,
        'Your changes are on the server.',
      ));
    } else {
      onToast?.(errorToast(
        [ok && `${ok} saved`, failed && `${failed} failed`, lost && `${lost} not loaded`]
          .filter(Boolean).join(', '),
        'The rows that did not save are still marked and still hold your changes.',
      ));
    }
  /* No `rowId` here. `save` reads ids out of the dirty buffer's own keys —
     the buffer is keyed by id — so it never calls `rowId` itself. Leaving it
     in the list only made `save` a new function whenever a page passed an
     inline `rowId`, which in turn churned every memo that depends on it. */
  }, [saving, onCommit, dirty, onToast, onSaved]);

  const failedIds = useMemo(
    () => [...status.entries()].filter(([, s]) => s === 'error').map(([id]) => id),
    [status],
  );

  /* A cell that fails a hard rule blocks the whole save. Sending it and
     letting the server refuse would cost a round trip to learn what we
     already knew, and would leave a red row that was never the user's fault
     to interpret. Soft warnings (col.warn) do not block — an odometer that
     went backwards is suspicious, not impossible. */
  const blocking = useMemo(() => {
    const out = [];
    dirty.forEach((changes, id) => {
      const row = seenRef.current.get(id);
      if (!row) return;
      /* The row a rule sees is the SAVED record with this buffer's edits laid
         over it — not the saved record alone. A cross-field rule ("a licence
         number needs an expiry date") reading only the stored row judges the
         edit against a value the user has already changed in the same pass:
         clear both the number and the date together and the rule still sees
         the old number, fires, and disables Save for the WHOLE grid, not just
         that row. There is then no way out but Discard, which throws away
         every other row's work too. */
      const merged = { ...row };
      Object.keys(changes).forEach(k => { merged[k] = changes[k].to; });
      Object.keys(changes).forEach(k => {
        const col = allColumns.find(c => c.key === k);
        if (!col || !col.validate) return;
        /* Third argument is the SAVED row, before any of this buffer's edits.
           A rule that compares the new value against the stored one — "is this
           odometer lower than the reading on record" — cannot use `merged`,
           because `merged[thisKey]` IS the new value, so the comparison reads
           `x < x` and never fires. Both rows, and the rule picks. */
        const msg = col.validate(changes[k].to, merged, row);
        if (msg) out.push({ id, row, col, msg });
      });
    });
    return out;
    /* `rows` and `rowId` are deliberately NOT dependencies: this body reads
       `dirty`, `seenRef` and `allColumns` only. `rows` is a fresh .filter()
       array on nearly every page, so listing it re-ran every dirty cell's
       validate on every render — including every keystroke, with a 40-row
       buffer. That is the exact cost the memoisation exists to avoid. */
  }, [dirty, allColumns]);

  // The row ids that a hard rule is currently blocking. "Save changes" already
  // refuses to send while `blocking` is non-empty, but both Retry buttons only
  // checked `saving` — so a row with, say, a blank name could be pushed to the
  // server through Retry, costing the round trip the gate exists to avoid.
  const blockedIds = useMemo(() => new Set(blocking.map(b => b.id)), [blocking]);

  /* ── Keyboard: one tab stop, arrows inside ───────────────────────────── */
  const cellRefs = useRef(new Map());
  const key = (r, c) => `${r}:${c}`;
  // Where focus is right now, readable from a callback without making that
  // callback change identity on every arrow press.
  const focusRef = useRef(focus);
  useEffect(() => { focusRef.current = focus; }, [focus]);

  /* Focus follows the roving cell — but only when focus was already inside
     the grid, so clicking away and then having a row arrive does not yank the
     caret back.

     `wasEditing` is what makes committing work. On Enter/Tab/Escape the editor
     unmounts, and React removes the focused <input> during the mutation phase
     — so by the time this layout effect runs, `document.activeElement` is
     already <body> and `contains()` is false. The guard failed, `el.focus()`
     never ran, and every keyboard commit dropped focus out of the table
     entirely: arrow keys went dead and the only way back in was the mouse.
     Mouse editing was unaffected, which is why it survived this long. */
  const wasEditing = useRef(false);
  useLayoutEffect(() => {
    if (editing) { wasEditing.current = true; return; }
    const el = cellRefs.current.get(key(focus.r, focus.c));
    const inside = rootRef.current?.contains(document.activeElement);
    if (el && (inside || wasEditing.current)) el.focus({ preventScroll: false });
    wasEditing.current = false;
  }, [focus, editing]);

  const move = useCallback((dr, dc, extend = false) => {
    // Anchor first, and outside the setFocus updater. A state updater has to
    // be pure — React runs it during render and runs it twice in development
    // — so setting other state from inside one drops or doubles the effect.
    if (extend) setAnchor(a => a || { ...focusRef.current });
    else setAnchor(null);
    setFocus(f => ({
      r: Math.max(0, Math.min(sorted.length - 1, f.r + dr)),
      c: Math.max(0, Math.min(columns.length - 1, f.c + dc)),
    }));
  }, [sorted.length, columns.length]);

  /* Hiding a column or filtering rows can leave focus pointing past the end,
     and then no cell holds tabIndex 0 — the grid silently stops being
     reachable by keyboard. Clamp it back. */
  useEffect(() => {
    setFocus(f => {
      const r = Math.min(f.r, Math.max(0, sorted.length - 1));
      const c = Math.min(f.c, Math.max(0, columns.length - 1));
      return r === f.r && c === f.c ? f : { r, c };
    });
  }, [sorted.length, columns.length]);

  /* A range anchored to a row that has since been filtered away points at
     whatever row now occupies that index — so the bar would offer to fill
     cells the user never marked. Dropping the anchor when the rows change is
     the range equivalent of the tick-pruning this replaces.

     Keyed on the row IDENTITIES, not on the `rows` array. Two of the three
     pages compute `rows` with a bare `.filter()` in their render body, so the
     array is a new object every parent render — and this app runs React Query
     with refetchOnWindowFocus. Keyed on `[rows]` the effect fired on every
     search keystroke, every filter change, and every alt-tab back to the
     window: mark a range, glance at the spreadsheet you are copying from,
     come back, and the range is gone. Which is the exact workflow the range
     exists for. */
  const rowSig = useMemo(
    () => sorted.map(r => String(rowId(r))).join('|'),
    [sorted, rowId],
  );
  /* The same argument applies sideways. `anchor.c` and `focus.c` are column
     INDICES, so hiding or reordering a column leaves the highlighted block
     aimed at different columns than the user marked — and then "set for all
     selected" offers to fill one they never touched. No crash (`rangeCells`
     guards), just a selection that silently re-aims. */
  const colSig = useMemo(() => columns.map(c => c.key).join('|'), [columns]);
  useEffect(() => { setAnchor(null); }, [rowSig, colSig]);

  /* `editing` carries the row's ID and the column's KEY, not just the indices.
     Position alone is a promise the data does not keep: a background refetch,
     a poll, or any invalidation can reorder `sorted` while an editor is open,
     and then index 4 is a different record. What the user saw was their
     half-typed text vanish and be replaced by another row's value, with the
     caret still in it — and a commit after that shift wrote what they typed
     onto a record they never opened.

     The range already learned this lesson; it is keyed on `rowSig` for exactly
     the same reason. The indices are kept as a fallback for the first paint. */
  const beginEdit = useCallback((seed) => {
    const col = columns[focus.c];
    const row = sorted[focus.r];
    if (!isEditable(col) || !row) return;
    setEditing({ r: focus.r, c: focus.c, seed, id: String(rowId(row)), key: col.key });
  }, [columns, sorted, focus, rowId]);

  const onKeyDown = useCallback((e) => {
    if (editing) return;

    /* This handler is bound to the whole <table>, so it also sees keys pressed
       on the controls INSIDE the table — the sort buttons in the header, the
       select-all checkbox, the columns gear, the per-row retry. It used to act
       on all of them:

         · Enter on a sort button ran `beginEdit` instead of sorting, so no
           column could be sorted from the keyboard at all.
         · Space on select-all fell through to the printable branch and opened
           an editor instead of ticking the box.
         · Worst of it — Enter on the gear ran the `btn.click()` branch below,
           which clicks whatever button sits in the CURRENTLY FOCUSED CELL. On
           Vehicles that column holds Delete. Pressing Enter on a settings
           button could open a delete confirmation for an unrelated row.

       A grid cell is the only thing whose keys belong to the grid. */
    const cellEl = e.target instanceof Element ? e.target.closest('[role="gridcell"]') : null;
    if (!cellEl || !rootRef.current?.contains(cellEl)) return;

    const mod = e.metaKey || e.ctrlKey;

    // Shift+Cmd+Z is redo elsewhere; taking it here just undid twice.
    if (mod && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
    if (mod && e.key === 'Home') { e.preventDefault(); setFocus({ r: 0, c: 0 }); setAnchor(null); return; }
    if (mod && e.key === 'End') {
      // Clamped: on an empty filtered grid `sorted.length - 1` is -1, which
      // left no cell holding tabIndex=0 and dropped the grid out of the tab
      // order until something else changed its length.
      e.preventDefault();
      setFocus({ r: Math.max(0, sorted.length - 1), c: Math.max(0, columns.length - 1) });
      setAnchor(null); return;
    }
    switch (e.key) {
      case 'ArrowUp':    e.preventDefault(); move(-1, 0, e.shiftKey); return;
      case 'ArrowDown':  e.preventDefault(); move(1, 0, e.shiftKey); return;
      case 'ArrowLeft':  e.preventDefault(); move(0, -1, e.shiftKey); return;
      case 'ArrowRight': e.preventDefault(); move(0, 1, e.shiftKey); return;
      case 'Home':       e.preventDefault(); setFocus(f => ({ ...f, c: 0 })); setAnchor(null); return;
      // Clamped like every other branch. With no visible columns this was -1,
      // which no cell can hold, so nothing carried tabIndex={0} and the grid
      // became unreachable by keyboard until a re-render moved focus.
      case 'End':        e.preventDefault(); setFocus(f => ({ ...f, c: Math.max(0, columns.length - 1) })); setAnchor(null); return;
      case 'PageDown':   e.preventDefault(); move(10, 0, e.shiftKey); return;
      case 'PageUp':     e.preventDefault(); move(-10, 0, e.shiftKey); return;
      /* Tab walks the row, wraps to the next row at the end of one, and — at
         the very last cell going forward or the very first going back — is
         LET THROUGH so focus can leave the table. It used to be
         unconditionally preventDefault-ed with a clamped move, which meant
         that once focus entered the grid there was no keyboard way out of it:
         a WCAG 2.1.2 keyboard trap, and it also made the per-row "Retry" the
         save bar offers unreachable. */
      case 'Tab': {
        const last = { r: sorted.length - 1, c: columns.length - 1 };
        const atEnd   = !e.shiftKey && focus.r >= last.r && focus.c >= last.c;
        const atStart =  e.shiftKey && focus.r <= 0 && focus.c <= 0;
        if (atEnd || atStart) return;          // let the browser move on
        e.preventDefault();
        if (!e.shiftKey && focus.c >= last.c) { setFocus({ r: focus.r + 1, c: 0 }); setAnchor(null); return; }
        if (e.shiftKey && focus.c <= 0)       { setFocus({ r: focus.r - 1, c: last.c }); setAnchor(null); return; }
        move(0, e.shiftKey ? -1 : 1);
        return;
      }
      case 'Enter':
      case 'F2': {
        /* On a read-only cell that holds a control — the Open / Docs actions
           — Enter presses it. Those cells cannot be edited, so Enter had
           nothing to do and the actions were mouse-only: the roving tabindex
           means the buttons themselves are never in the tab order. */
        const cell = cellRefs.current.get(key(focus.r, focus.c));
        const btn = cell?.querySelector('button');
        if (btn && !isEditable(columns[focus.c])) { e.preventDefault(); btn.click(); return; }
        e.preventDefault(); beginEdit(); return;
      }
      case 'Escape':     setAnchor(null); return;
      case ' ':
        /* Shift+Space used to tick the row. There are no ticks now, so it
           selects the row's cells instead — the same idea in the new
           selection model, and still the documented grid shortcut. Plain
           Space falls through to the printable branch and starts an edit;
           returning here let the browser scroll the page, which is the
           opposite of what a spreadsheet does. */
        if (e.shiftKey) {
          e.preventDefault();
          setAnchor({ r: focus.r, c: 0 });
          setFocus(f => ({ ...f, c: columns.length - 1 }));
          return;
        }
        break;
      default: break;
    }
    // Any printable character starts an edit and replaces what was there.
    if (!mod && e.key.length === 1) { e.preventDefault(); beginEdit(e.key); }
  }, [editing, move, beginEdit, columns, sorted, focus.r, focus.c, undo]);

  /* ── Range: shift-click / shift-arrows, then type or paste into all ──── */
  const range = useMemo(() => {
    if (!anchor) return null;
    return {
      r0: Math.min(anchor.r, focus.r), r1: Math.max(anchor.r, focus.r),
      c0: Math.min(anchor.c, focus.c), c1: Math.max(anchor.c, focus.c),
    };
  }, [anchor, focus]);

  const inRange = useCallback((r, c) => (
    range && r >= range.r0 && r <= range.r1 && c >= range.c0 && c <= range.c1
  ), [range]);

  const commitEdit = useCallback((raw, changed = true) => {
    // `editing` can already be null here: CellEditor's onBlur fires when the
    // input is removed from the DOM, so the Enter path could re-enter this
    // after setEditing(null) — throwing, or pushing a second undo entry for
    // one edit.
    if (!editing) return;
    /* Resolve by identity, fall back to position for an editor opened before
       this carried an id (double-click, below). `seenRef` is the last resort:
       if the row has left `sorted` entirely — filtered away by a refetch while
       the user was typing — the edit still belongs to that record, and the
       buffer already outlives the filter by design. */
    const col = (editing.key && allColumns.find(c => c.key === editing.key)) || columns[editing.c];
    const row = (editing.id && (sorted.find(r => String(rowId(r)) === editing.id) || seenRef.current.get(editing.id)))
             || sorted[editing.r];
    if (!col || !row) { setEditing(null); return; }

    /* Confirming an editor without entering anything writes nothing at all.

       Two things were wrong without this. A range under the editor fanned the
       untouched, pre-filled value across every cell it covered, so Enter twice
       rewrote five records. And every open-and-close of an editor — a stray
       double-click, a date cell opened just to read it — pushed an undo entry
       that `setCell` then discarded as a non-change, leaving Ctrl+Z presses
       that visibly did nothing before reaching the real edit.

       Escape already cancels. This makes Enter, Tab and blur agree with it
       when there is nothing to commit. */
    if (!changed) { setEditing(null); return; }

    // A range under an edit means "set all of these".
    if (range && (range.r0 !== range.r1 || range.c0 !== range.c1)) {
      const group = `range:${nextGroup()}`;
      for (let r = range.r0; r <= range.r1; r += 1) {
        for (let c = range.c0; c <= range.c1; c += 1) {
          const cc = columns[c]; const rr = sorted[r];
          /* Re-parsed per target column, from the RAW text. It used to parse
             once against the anchor column and write that single value
             everywhere — so a range spanning Status and Fleet (both selects)
             wrote the literal 'active' into fleetId, and parseValue keeps an
             unrecognised select value on purpose, so nothing rejected it. */
          if (cc && rr && isEditable(cc) && cc.type === col.type) {
            setCell(rr, cc, parseValue(cc, raw, rr), { group });
          }
        }
      }
    } else {
      setCell(row, col, parseValue(col, raw, row));
    }
    setEditing(null);
  }, [columns, allColumns, sorted, rowId, editing, range, setCell]);

  /* ── Paste from a spreadsheet ────────────────────────────────────────── */
  const [pasting, setPasting] = useState(null);
  const onPaste = useCallback((e) => {
    if (editing) return;
    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const grid = parseClipboardGrid(text);
    /* A clipboard holding nothing but whitespace parses to one empty cell,
       which would plan a change that BLANKS the focused cell — so copying a
       stray newline silently queued a deletion. Nothing to paste is nothing
       to do. */
    if (grid.length === 1 && grid[0].length === 1 && grid[0][0] === '') return;
    const plan = [];
    // Counted, not shrugged off. See `dropped` in the dialog.
    let overflow = 0;
    grid.forEach((line, dr) => line.forEach((raw, dc) => {
      const row = sorted[focus.r + dr];
      const col = columns[focus.c + dc];
      if (!row || !col) { overflow += 1; return; }
      if (!isEditable(col)) return;
      const next = parseValue(col, raw, row);
      // Against what the cell currently shows, not against the server's
      // copy. Pasting a value back over an unsaved edit is a real change —
      // comparing to the record made it look like a no-op and skipped it,
      // so the paste silently did nothing.
      const now = valueOf(row, col);
      if (sameValue(col, row, now, next)) return;
      plan.push({ row, col, next, raw, from: now });
    }));
    if (!plan.length && !overflow) return;
    setPasting({ cells: plan, overflow });
  }, [editing, sorted, columns, focus, valueOf]);

  const applyPaste = useCallback(() => {
    const group = `paste:${nextGroup()}`;
    pasting.cells.forEach(p => setCell(p.row, p.col, p.next, { group }));
    setPasting(null);
  }, [pasting, setCell]);

  /* ── Bulk: set every editable cell in the selected range ───────────────
     There used to be a checkbox column, and the bulk bar acted on ticked
     rows. The ticks were a second selection model sitting next to the one a
     spreadsheet already has — you could have three rows ticked and a range
     highlighted at the same time, meaning two different things, with only the
     bar's wording to tell you which was about to be written.

     So the range IS the selection now. Click a cell, shift-click another (or
     shift-arrow), and the bar offers to fill what you have marked out. Same
     gesture as Excel, one less column of chrome, and nothing to leave ticked
     by accident. */
  /* The width a column is actually rendered at.

     Every column gets exactly what it asks for, and any SLACK — the difference
     between the sum of those widths and the scroller — goes to ONE column
     rather than being spread across all of them by the fixed-layout algorithm.
     The identity column takes it: it is the leftmost, it is frozen, so growing
     it moves nothing else, and a name is the one value in a row that genuinely
     benefits from more room. Everything to its right keeps the width it was
     designed at, so a price stays under its own heading and "Clear" stays
     beside the row it belongs to instead of floating in the middle of a cell.

     Slack is only ever added, never subtracted: when the columns already
     overflow the table scrolls, exactly as before. And while a resize drag is
     live the slack is frozen, so the column tracks the pointer instead of
     fighting it. */
  /* The identity column takes the slack, but only up to a point. Unbounded, a
     seven-column table on a 27-inch monitor gave the name column 900px of air
     to hold one short word — the same dead space as before, just moved into
     one place. Past the cap the remainder is shared by the middle columns, so
     the table still reaches the right edge without any single cell looking
     abandoned. Pinned columns never grow: the identity column is handled
     explicitly, and the actions column is a fixed set of buttons. */
  const SLACK_CAP = 260;
  /* `dragW` is deliberately NOT a dependency, and not an early return.

     Zeroing the slack for the duration of a drag was meant to "freeze" it, but
     zeroing is not freezing: on the first mousemove every other column
     collapsed to its base width (the identity column measured 461px → 255px),
     the declared widths stopped summing to the container, and fixed-layout
     handed the surplus back out proportionally — to the dragged column too. So
     the table lurched on the first pixel and the divider never tracked the
     pointer.

     Nothing this memo reads actually changes mid-drag: `colState.widths` is
     written once on mouseup, and `columns` and `scrollW` are untouched. Simply
     leaving `dragW` out makes the value constant for the whole drag — a real
     freeze, with no snapshot to keep in sync. */
  const spread = useMemo(() => {
    if (!scrollW) return { first: 0, each: 0 };
    const total = columns.reduce((n, c) => n + baseWidth(c), 0);
    const free = Math.max(0, scrollW - total);
    const first = Math.min(free, SLACK_CAP);
    const middle = columns.filter((c, i) => i > 0 && !c.frozen && !c.frozenEnd).length;
    return { first, each: middle ? Math.floor((free - first) / middle) : 0 };
  }, [scrollW, columns, baseWidth]);

  /* The live drag value replaces the BASE width and then takes its slack like
     any other column, rather than being returned as the finished width. That
     keeps one definition of "rendered width = base + slack" for every column
     in every state, so there is no step at mousedown (slack suddenly dropped)
     or at mouseup (slack suddenly re-added). */
  const widthOf = useCallback((col, index) => {
    const base = (dragW && dragW.key === col.key) ? dragW.w : baseWidth(col);
    if (index === 0) return base + spread.first;
    if (col.frozen || col.frozenEnd) return base;
    return base + spread.each;
  }, [dragW, baseWidth, spread]);

  /* Where the Columns trigger renders. The page hands us a ref to an empty
     span in its header. A ref object carries no signal when it fills, so the
     element has to be copied into state to cause the render that portals into
     it.

     useLayoutEffect, not useEffect: React attaches refs during the commit,
     before layout effects run, so `toolsSlot.current` is already the span by
     the time this fires — and running before paint means the header never
     paints one frame without its button. With useEffect the button visibly
     popped in after the rest of the header.

     `[toolsSlot]` is the honest dependency. The span is rendered
     unconditionally by every page, so it cannot swap out underneath us; the
     bail-when-unchanged keeps this a no-op if a page ever passes a new ref
     object for the same element. */
  const [colsSlot, setColsSlot] = useState(null);
  useLayoutEffect(() => {
    setColsSlot(prev => {
      const next = toolsSlot?.current || null;
      return prev === next ? prev : next;
    });
  }, [toolsSlot]);

  const columnsButton = (
    <button type="button" className="dg-colbtn" ref={gearRef}
            onClick={() => setShowCols(v => !v)}
            aria-expanded={showCols} aria-haspopup="dialog">
      <Columns size={14} />
      <span>Columns</span>
      {colState.hidden.length > 0 && (
        <i title={`${colState.hidden.length} hidden`}>{columns.length}/{ordered.length}</i>
      )}
    </button>
  );

  const rangeCells = useMemo(() => {
    if (!range) return [];
    const out = [];
    for (let r = range.r0; r <= range.r1; r += 1) {
      for (let c = range.c0; c <= range.c1; c += 1) {
        const row = sorted[r], col = columns[c];
        if (row && col && isEditable(col)) out.push({ row, col });
      }
    }
    return out;
  }, [range, sorted, columns]);

  /* The columns the range spans that are marked `bulk`, deduped and in order.

     `bulk` is the page's statement that a column is safe to set twenty rows
     of at once. Offering every editable column instead put "Set name…" on
     the bar for a range dragged through the frozen Name column — and `name`
     carries a hard validate, so twenty rows would come back as twenty errors
     to repair by hand. Typing into a range still fills whatever it covers;
     that is one deliberate keystroke, not a button promising it is fine. */
  const rangeCols = useMemo(() => {
    const seen = new Set(); const out = [];
    rangeCells.forEach(({ col }) => {
      if (col.bulk && !seen.has(col.key)) { seen.add(col.key); out.push(col); }
    });
    return out;
  }, [rangeCells]);

  const rangeRowCount = range ? (range.r1 - range.r0 + 1) : 0;
  const hasRange = rangeCells.length > 1;

  const [bulkCol, setBulkCol] = useState(null);
  const applyBulk = useCallback((col, raw) => {
    const group = `bulk:${nextGroup()}`;
    // Parsed per row, like the range fill an edit performs — a select's raw
    // text has to be resolved against the column it is landing in.
    rangeCells
      .filter(({ col: c }) => c.key === col.key)
      .forEach(({ row, col: c }) => setCell(row, c, parseValue(c, raw, row), { group }));
    setBulkCol(null);
  }, [rangeCells, setCell]);

  /* ── Render ──────────────────────────────────────────────────────────── */
  /* The page's own zero-state replaces the table — but only when there is
     nothing unsaved. A filter that narrows `rows` to nothing while edits are
     pending used to take the save bar down with the table, so the Save button
     and the change count vanished over a live buffer; the edits survived in
     state, but navigating away lost them with no warning. */
  if (!rows.length && empty && dirty.size === 0) return empty;

  return (
    <div className="dg-wrap">
      {/* Bulk bar. No delete: twenty deletes over twenty requests with no
          transaction and no undo is the one bulk action that goes wrong
          quietly. Single-row delete stays where it always was. */}
      {hasRange && (
        <div className="dg-bulk" role="status">
          <b>
            {rangeCells.length} cell{rangeCells.length === 1 ? '' : 's'}
            {rangeRowCount > 1 && ` · ${rangeRowCount} rows`}
          </b>
          {rangeCols.map(col => (
            <button key={col.key} type="button" className="dg-bulk-btn"
                    onClick={() => setBulkCol(bulkCol === col.key ? null : col.key)}>
              Set {col.label.toLowerCase()}…
            </button>
          ))}
          {bulkCol && (() => {
            const col = rangeCols.find(c => c.key === bulkCol);
            if (!col) return null;
            const n = rangeCells.filter(({ col: c }) => c.key === col.key).length;
            return (
              <span className="dg-bulk-field">
                {col.type === 'select' ? (
                  <select autoFocus defaultValue="" onChange={e => e.target.value !== '' && applyBulk(col, e.target.value)}>
                    <option value="" disabled>Choose {col.label.toLowerCase()}…</option>
                    {(col.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input autoFocus type={col.type === 'number' ? 'number' : 'text'}
                         placeholder={`${col.label} for all ${n}`}
                         onKeyDown={e => { if (e.key === 'Enter') applyBulk(col, e.target.value); if (e.key === 'Escape') setBulkCol(null); }} />
                )}
              </span>
            );
          })()}
          <span className="dg-sp" />
          {/* Or just type — a range under an edit already fills the whole
              range, so the bar is the discoverable version of a gesture that
              works without it. */}
          <span className="dg-bulk-hint">or type to fill</span>
          <button type="button" className="dg-bulk-btn" onClick={() => { setAnchor(null); setBulkCol(null); }}>
            Clear
          </button>
        </div>
      )}

      {/* The Columns control, rendered into the PAGE HEADER when the page
          offers a slot for it.

          This used to be a strip of its own between the header and the table.
          Once the `note` and `hint` came out of that strip — both of them
          saying things the table already said — all that was left was one
          button and a whole row of white space to hold it. A band that exists
          to carry a single control is the same waste, just smaller.

          So the button goes up beside the page's other controls, where it
          already belonged, and the strip disappears. DataGrid keeps ownership
          of the column state and the panel; the page only supplies a place to
          put the trigger. The panel itself is positioned against `.dg-body`,
          not the button, so moving the trigger does not move the menu.

          A page that passes no slot still gets the old strip — nothing breaks
          if a future grid forgets. */}
      {colsSlot
        ? createPortal(columnsButton, colsSlot)
        : <div className="dg-tools"><span className="dg-tools-sp" />{columnsButton}</div>}

      <div className="dg-body">
        {showCols && (
          <ColumnMenu
            all={ordered}
            hidden={colState.hidden}
            onToggle={(k, on) => setColState(s => ({
              ...s, hidden: on ? [...s.hidden, k] : s.hidden.filter(x => x !== k),
            }))}
            onReorder={(order) => setColState(s => ({ ...s, order }))}
            onReset={() => setColState(defaultState(allColumns))}
            onClose={() => setShowCols(false)}
            triggerRef={gearRef}
          />
        )}

      <div className="dg-scroll" ref={scrollRef} onScroll={onScrollX}>
        {/* No aria-rowcount / aria-colcount / aria-colindex. Those exist for
            grids whose DOM is incomplete — virtualised or paged. Here every
            row is rendered, and a hidden column is genuinely not part of the
            grid, so the browser's own count is the true one. Declaring them
            by hand only created a second source of truth that went wrong the
            moment columns were reordered. */}
        {/* `dg-nopick` unconditionally: there is no checkbox column any more.
            Selecting a range of cells is the selection, so a per-row tick was
            a second way to mean the same thing — and a column of chrome on
            every screen to support it. */}
        <table
          className={`dg dg-nopick${xEdges.start ? ' is-xstart' : ''}${xEdges.end ? ' is-xend' : ''}`}
          role="grid"
          ref={rootRef}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        >
          <thead>
            <tr>
              {columns.map((col, ci) => {
                const on = sort.key === col.key;
                return (
                  <th
                    key={col.key} scope="col"
                    /* A column can carry a `note` of its own: something true
                       about THIS column that the cells cannot show. It is a
                       tooltip, not a band — it costs no vertical space and it
                       sits on the thing it describes. The only current use is
                       the contact mirror on Customers, Vendors and Drivers,
                       where editing a name also writes to a second record. */
                    title={col.note || undefined}
                    aria-sort={col.label
                      ? (on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none')
                      : undefined}
                    className={[
                      col.align === 'end' ? 'num' : '',
                      col.frozen ? 'dg-frozen' : '',
                      col.frozenEnd ? 'dg-frozen-end' : '',
                      resizing === col.key ? 'is-resizing' : '',
                      dragCol === col.key ? 'is-dragging' : '',
                      dropCol?.key === col.key ? (dropCol.after ? 'is-dropzone-after' : 'is-dropzone') : '',
                    ].filter(Boolean).join(' ')}
                    style={{ width: widthOf(col, ci) }}
                    draggable={canReorder(col)}
                    onDragStart={canReorder(col) ? (e) => {
                      // A resize in progress must not also start a reorder.
                      // preventDefault on the handle's mousedown usually stops
                      // this, but dragstart is a separate event on the <th>.
                      if (resizeRef.current) { e.preventDefault(); return; }
                      setDragCol(col.key);
                      e.dataTransfer.effectAllowed = 'move';
                      // Firefox will not start a drag without payload.
                      e.dataTransfer.setData('text/plain', col.key);
                    } : undefined}
                    onDragOver={canReorder(col) && dragCol ? (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      /* Which edge the line goes on has to match where the
                         column will actually land. A rightward drag lands
                         AFTER the target, so drawing the line on the target's
                         leading edge promised the opposite of what happened. */
                      const order = columns.map(c => c.key);
                      const after = order.indexOf(dragCol) < order.indexOf(col.key);
                      if (dropCol?.key !== col.key || dropCol?.after !== after) {
                        setDropCol({ key: col.key, after });
                      }
                    } : undefined}
                    onDrop={canReorder(col) && dragCol ? (e) => { e.preventDefault(); onHeaderDrop(col.key); } : undefined}
                    onDragEnd={() => { setDragCol(null); setDropCol(null); }}
                  >
                    {/* A column with no label is an action column. Giving it
                        a sort button would put an empty control in the header
                        that sorts by nothing. */}
                    {col.label ? (
                      <button type="button" className="dg-sortbtn"
                              onClick={() => setSort(s => ({
                                key: col.key,
                                dir: s.key === col.key && s.dir === 'asc' ? 'desc' : 'asc',
                              }))}>
                        {col.label}
                        {on ? (sort.dir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
                            : <ChevronsUpDown size={11} className="dg-sortdim" />}
                      </button>
                    ) : <span className="dg-sortbtn" aria-hidden="true" />}
                    {col.unit && <i className="dg-unit">{col.unit}</i>}
                    {/* Not on the pinned-right column: its divider would sit
                        on the table's own edge. */}
                    {!col.frozenEnd && (
                      <span
                        className="dg-resize"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${col.label || 'column'}`}
                        onMouseDown={(e) => startResize(e, col)}
                        onDoubleClick={() => setColState(s => {
                          // Back to whatever the code specifies.
                          const w = { ...s.widths }; delete w[col.key];
                          return { ...s, widths: w };
                        })}
                      />
                    )}
                  </th>
                );
              })}

            </tr>
          </thead>
          <tbody>
            {/* Column headings over nothing is the worst answer to "why is
                this empty". The page's own empty state covers "no drivers
                at all"; this covers "your filters excluded everyone". */}
            {sorted.length === 0 && (
              <tr className="dg-none">
                <td colSpan={columns.length}>{emptyFiltered}</td>
              </tr>
            )}
            {sorted.map((row, ri) => {
              const id = String(rowId(row));
              const rowDirty = dirty.get(id);
              /* The record as the USER has it — every pending edit applied over
                 the server's copy. `validate` and `warn` are handed this, not
                 the raw row, because that is what the Save gate (`blocking`)
                 already uses. With the two disagreeing, a cross-field rule
                 could disable Save with no red cell anywhere to explain it, or
                 redden a cell while Save stayed happily enabled. */
              const merged = rowDirty
                ? Object.keys(rowDirty).reduce((acc, k) => { acc[k] = rowDirty[k].to; return acc; }, { ...row })
                : row;
              const st = status.get(id);
              const why = errors.get(id);
              return (
                <React.Fragment key={id}>
                <tr className={st ? `is-${st}` : ''}>
                  {columns.map((col, ci) => {
                    const changed = rowDirty && Object.prototype.hasOwnProperty.call(rowDirty, col.key);
                    const focused = focus.r === ri && focus.c === ci;
                    /* Identity, not position — so the editor stays with the
                       record it was opened on when the rows move under it. */
                    const isEditingHere = editing && (editing.id
                      ? (editing.id === id && editing.key === col.key)
                      : (editing.r === ri && editing.c === ci));
                    const v = valueOf(row, col);
                    // Both severities apply only to something the user just
                    // typed. A record that arrived without a name is a fact
                    // about the data, not a mistake the user is making right
                    // now — and reddening it would also be a lie, because a
                    // cell nobody touched can never block a save.
                    // (value, rowAsEdited, rowAsSaved) — see `blocking`.
                    const bad  = changed && col.validate ? col.validate(v, merged, row) : null;
                    const soft = !bad && changed && col.warn ? col.warn(v, merged, row) : null;
                    return (
                      <td
                        key={col.key}
                        role="gridcell"
                        aria-colindex={ci + 1}
                        aria-readonly={!isEditable(col) || undefined}
                        aria-selected={inRange(ri, ci) || undefined}
                        tabIndex={focused && !editing ? 0 : -1}
                        ref={el => { if (el) cellRefs.current.set(key(ri, ci), el); else cellRefs.current.delete(key(ri, ci)); }}
                        className={[
                          col.align === 'end' ? 'num' : '',
                          col.frozen ? 'dg-frozen' : '',
                          col.frozenEnd ? 'dg-frozen-end' : '',
                          !isEditable(col) ? 'is-ro' : '',
                          changed ? 'is-dirty' : '',
                          inRange(ri, ci) ? 'is-range' : '',
                          bad ? 'is-bad' : '',
                          soft ? 'is-warn' : '',
                        ].filter(Boolean).join(' ')}
                        title={bad || soft || undefined}
                        onMouseDown={(e) => {
                          if (e.shiftKey) { setAnchor(a => a || { ...focus }); setFocus({ r: ri, c: ci }); e.preventDefault(); return; }
                          setAnchor(null); setFocus({ r: ri, c: ci });
                        }}
                        onDoubleClick={() => isEditable(col) && setEditing({ r: ri, c: ci, id, key: col.key })}
                      >
                        {/* The non-editing branch carries a `title` on any
                            plain-text cell, so a column too narrow for its
                            content truncates RECOVERABLY. Prose columns — a
                            description, an address, a note — always outrun
                            whatever width you pick, and an ellipsis with no
                            way to read the rest is what makes a table feel
                            broken. Only when the column has no custom
                            renderer and the value is a plain scalar: a
                            rendered cell owns its own tooltip, and
                            stringifying a React element would put
                            "[object Object]" in the hover. */}
                        {isEditingHere ? (
                          <CellEditor col={col} row={row} seed={editing.seed} value={v}
                                      onCancel={() => setEditing(null)}
                                      onCommit={commitEdit}
                                      onCommitAndMove={(raw, dr, dc, changed) => { commitEdit(raw, changed); move(dr, dc); }} />
                        ) : (
                          <span
                            className="dg-cell"
                            title={!col.render && (typeof v === 'string' || typeof v === 'number')
                              ? String(v)
                              : undefined}
                          >
                            {col.render ? col.render(row, v) : displayValue(col, row, v)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
                {/* The server's own words, on the row that failed. A toast
                    scrolls away and never says which row it meant. */}
                {why && (
                  <tr className="dg-why">
                    <td colSpan={columns.length}>
                      <span>{rowLabel(row)} was not saved — {why}</span>
                      <button type="button" className="dg-bulk-btn" disabled={saving || blockedIds.has(id)}
                              onClick={() => save([id])}>Retry this row</button>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      {/* The save bar. Present only when there is something to lose. */}
      {(dirtyCount > 0 || failedIds.length > 0) && (
        <div className={`dg-save ${failedIds.length ? 'has-fail' : ''}`} role="status">
          <b>
            {dirtyCount} change{dirtyCount === 1 ? '' : 's'} across{' '}
            {dirty.size} row{dirty.size === 1 ? '' : 's'}
          </b>
          {failedIds.length > 0 && (
            <span className="dg-failnote">
              · {failedIds.length} failed to save and {failedIds.length === 1 ? 'is' : 'are'} still here
            </span>
          )}
          {blocking.length > 0 && (
            <span className="dg-failnote">
              · {blocking[0].col.label} on {rowLabel(blocking[0].row)}: {blocking[0].msg}
              {blocking.length > 1 && ` (+${blocking.length - 1} more)`}
            </span>
          )}
          <span className="dg-sp" />
          {failedIds.length > 0 && (() => {
            // Only the failed rows that would actually be accepted. Retrying a
            // row a hard rule is blocking spends a round trip to be told what
            // the grid already knows.
            const retryable = failedIds.filter(id => !blockedIds.has(id));
            return (
              <button type="button" className="dg-sec" disabled={saving || retryable.length === 0}
                      onClick={() => save(retryable)}>
                Retry failed ({retryable.length})
              </button>
            );
          })()}
          <button type="button" className="dg-sec" disabled={saving} onClick={discard}>Discard</button>
          <button type="button" className="dg-pri"
                  disabled={saving || !dirtyCount || blocking.length > 0}
                  title={blocking.length ? blocking[0].msg : undefined}
                  onClick={() => save()}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}

      {/* Paste is the highest blast-radius input on the page and nobody else
          confirms it. This one does. */}
      {pasting && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPasting(null)}>
          <div className="dg-paste" role="dialog" aria-modal="true" aria-labelledby="dg-paste-t">
            <h2 id="dg-paste-t">Paste {pasting.cells.length} value{pasting.cells.length === 1 ? '' : 's'}?</h2>
            <p>
              This changes {pasting.cells.length} cell{pasting.cells.length === 1 ? '' : 's'} across{' '}
              {new Set(pasting.cells.map(p => String(rowId(p.row)))).size} row
              {new Set(pasting.cells.map(p => String(rowId(p.row)))).size === 1 ? '' : 's'}. Nothing is saved until you press Save.
            </p>
            {/* Say what did not fit. Pasting a 12-row block onto the third-from-
                last row used to drop nine rows on the floor and report only the
                three that landed — a count computed AFTER the truncation, so
                nothing on screen admitted the rest had gone. */}
            {pasting.overflow > 0 && (
              <p className="dg-paste-warn">
                {pasting.overflow} value{pasting.overflow === 1 ? '' : 's'} fell outside the table and will be
                ignored. Move the cursor up or left to fit the whole block.
              </p>
            )}
            <div className="dg-paste-list">
              {pasting.cells.slice(0, 8).map((p, i) => (
                <div key={i}>
                  <b>{rowLabel(p.row)}</b>
                  <span>{p.col.label}</span>
                  <i>{displayValue(p.col, p.row, p.from) || '—'} → {displayValue(p.col, p.row, p.next) || '—'}</i>
                </div>
              ))}
              {pasting.cells.length > 8 && <div className="dg-paste-more">and {pasting.cells.length - 8} more</div>}
            </div>
            <div className="dg-paste-foot">
              <button type="button" className="dg-sec" onClick={() => setPasting(null)}>
                <X size={13} /> Cancel
              </button>
              <button type="button" className="dg-pri" onClick={applyPaste} disabled={pasting.cells.length === 0}>Paste</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── The columns panel ───────────────────────────────────────────────────────
   This is the control that decides what the table is, so it is a panel with
   a name and a shape rather than a loose row of checkboxes. Each line is the
   column: grip, tick, label. Drag to reorder — the order here is the order
   left to right, so the panel is a picture of the table's own layout.

   Keyboard users are not left with a mouse-only feature: the same reorder is
   on alt+↑ / alt+↓ while a line has focus. */
function ColumnMenu({ all, hidden, onToggle, onReorder, onReset, onClose, triggerRef }) {
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);
  const ref = useRef(null);

  // Clicking anywhere else, or pressing Escape, puts it away. The button that
  // opened it is excluded: without that, its own mousedown closed the panel
  // and its click reopened it, so the control could never put it away.
  useEffect(() => {
    const away = (e) => {
      if (triggerRef?.current && triggerRef.current.contains(e.target)) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [onClose, triggerRef]);

  // The pinned columns hold the two edges; nothing crosses them.
  const floor = all.filter(c => c.frozen).length;
  const ceil = all.length - all.filter(c => c.frozenEnd).length;

  const move = (key, delta) => {
    const keys = all.map(c => c.key);
    const from = keys.indexOf(key);
    const to = from + delta;
    if (from < floor || from >= ceil || to < floor || to >= ceil) return;
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    onReorder(keys);
  };

  const drop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) { setDragKey(null); setOverKey(null); return; }
    const keys = all.map(c => c.key);
    const from = keys.indexOf(dragKey);
    const to = keys.indexOf(targetKey);
    if (from < floor || from >= ceil || to < floor || to >= ceil) {
      setDragKey(null); setOverKey(null); return;
    }
    // Compare the two ORIGINAL positions to decide which side of the target
    // to land on. Comparing the original `from` against the post-removal
    // index made a downward drag of one place a no-op.
    const after = to > from;
    keys.splice(from, 1);
    const landing = keys.indexOf(targetKey);
    keys.splice(after ? landing + 1 : landing, 0, dragKey);
    onReorder(keys);
    setDragKey(null); setOverKey(null);
  };

  const shown = all.length - hidden.length;

  /* Rendered on document.body, pinned under the button that opened it.

     It used to be absolutely positioned inside `.dg-body`, and that sits in a
     `.section-card` with `overflow: hidden` (there for the rounded corners).
     So the panel was clipped at the card's bottom edge: on a grid with two
     rows you saw the header and one line of the list, and no way to scroll
     to the rest. A menu's size must come from its own contents, not from
     how many rows the table behind it happens to have. */
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const r = triggerRef?.current?.getBoundingClientRect();
      if (!r) return;
      const top = r.bottom + 6;
      setPos({
        top,
        right: Math.max(12, window.innerWidth - r.right),
        // Never taller than the room left below the button.
        maxHeight: Math.max(160, Math.min(420, window.innerHeight - top - 12)),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [triggerRef]);

  return createPortal(
    <div className="dg-colpanel is-floating" ref={ref} role="dialog" aria-label="Columns"
         style={pos ? { top: pos.top, right: pos.right, maxBlockSize: pos.maxHeight } : { visibility: 'hidden' }}>
      <header>
        <b>Columns</b>
        <span>{shown} of {all.length} shown</span>
        <button type="button" onClick={onClose} aria-label="Close"><X size={13} /></button>
      </header>
      <p>Tick to show. Drag to reorder — top to bottom is left to right.</p>
      <ul>
        {all.map((c) => {
          const on = !hidden.includes(c.key);
          const pinned = c.frozen || c.frozenEnd;
          const where = c.frozen ? 'always first' : c.frozenEnd ? 'always last' : null;
          return (
            <li
              key={c.key}
              className={[
                on ? 'is-on' : '',
                pinned ? 'is-locked' : '',
                dragKey === c.key ? 'is-dragging' : '',
                overKey === c.key ? 'is-over' : '',
              ].filter(Boolean).join(' ')}
              draggable={!pinned}
              onDragStart={() => setDragKey(c.key)}
              onDragOver={(e) => { e.preventDefault(); setOverKey(c.key); }}
              onDragLeave={() => setOverKey(k => (k === c.key ? null : k))}
              onDrop={(e) => { e.preventDefault(); drop(c.key); }}
              onDragEnd={() => { setDragKey(null); setOverKey(null); }}
              onKeyDown={(e) => {
                if (!e.altKey) return;
                if (e.key === 'ArrowUp')   { e.preventDefault(); move(c.key, -1); }
                if (e.key === 'ArrowDown') { e.preventDefault(); move(c.key, 1); }
              }}
              tabIndex={pinned ? -1 : 0}
              aria-label={`${c.label || 'Actions'}, ${where || 'alt and arrow keys to reorder'}`}
            >
              <span className="dg-grip" aria-hidden="true">
                <i /><i /><i /><i /><i /><i />
              </span>
              <label>
                <input type="checkbox" checked={on} disabled={pinned}
                       onChange={() => onToggle(c.key, on)} />
                <span>{c.label || 'Actions'}</span>
              </label>
              {where && <em>{where}</em>}
              {c.unit && !pinned && <em>{c.unit}</em>}
            </li>
          );
        })}
      </ul>
      <footer>
        <button type="button" onClick={onReset}>Reset to default</button>
      </footer>
    </div>,
    document.body,
  );
}

/* The editor keeps its own value so a keystroke re-renders one cell, not the
   whole table. */
function CellEditor({ col, row, seed, value, onCancel, onCommit, onCommitAndMove }) {
  const [v, setV] = useState(() => {
    if (seed !== undefined) return seed;
    if (col.type === 'select') return value ?? '';
    if (value == null) return '';
    /* The editor opens on the value in its own editable form, not the raw
       record. An <input type="date"> silently renders BLANK when handed
       2026-07-23T00:00:00 — so every date cell opened empty, and the date
       you were about to change was the one thing you could not see. */
    if (col.type === 'date') return String(value).slice(0, 10);
    // `parse(raw, row)` — every other call site passes the row (`parseValue`,
    // `sameValue`), and a column whose parse reads it threw here on open.
    return String(col.parse ? col.parse(value, row) : value);
  });
  const ref = useRef(null);
  useLayoutEffect(() => { ref.current?.focus(); if (ref.current?.select && seed === undefined) ref.current.select(); }, [seed]);

  /* Did the user actually enter anything?

     Opened by typing (`seed` is the character they pressed) always counts.
     Opened by Enter or double-click, the editor is pre-filled with the current
     value, so it only counts once that text changes.

     Every commit reports this, because "committed" and "changed" are not the
     same event and the grid has to tell them apart. Enter twice over a
     multi-cell range — open the editor, confirm it — was fanning the
     pre-filled value across every row in the range: five records rewritten by
     a keystroke that in any other grid means "done, move down". */
  const opened = useRef(v);
  const changed = seed !== undefined || v !== opened.current;

  const keys = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === 'Enter')  { e.preventDefault(); e.stopPropagation(); onCommitAndMove(v, 1, 0, changed); return; }
    if (e.key === 'Tab')    { e.preventDefault(); e.stopPropagation(); onCommitAndMove(v, 0, e.shiftKey ? -1 : 1, changed); return; }
    e.stopPropagation();
  };

  if (col.type === 'select') {
    const known = (col.options || []).some(o => String(o.value) === String(value));
    return (
      <select ref={ref} className="dg-edit" value={v} onKeyDown={keys}
              onChange={e => { setV(e.target.value); onCommit(e.target.value, true); }}
              onBlur={() => onCommit(v, changed)}>
        <option value="">—</option>
        {/* Whatever is already in the record, even if it is not on the list.
            Without this the select would open showing nothing selected and
            blur would write that nothing back. */}
        {!known && value != null && value !== '' && (
          <option value={value}>{String(value)} — not on the list</option>
        )}
        {(col.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <input
      ref={ref}
      className="dg-edit"
      type={col.type === 'date' ? 'date' : col.type === 'number' ? 'number' : 'text'}
      step={col.type === 'number' ? (col.step || 'any') : undefined}
      value={v}
      onChange={e => setV(e.target.value)}
      onKeyDown={keys}
      onBlur={() => onCommit(v, changed)}
      aria-label={`${col.label} for ${row.name || 'this row'}`}
    />
  );
}
